/**
 * Exportaciones de facturas de un periodo (3-11-2026): «Descargar el mes» en Excel
 * (una hoja de facturas, una fila por factura, y otra de líneas si alguna factura las trae) y
 * CSV para importar facturas de proveedor en QuickBooks Online. Funciones puras: la carga del
 * historial y la descarga viven en `components/MonthExportModal.jsx`.
 *
 * Los campos se buscan por nombre con sinónimos en varios idiomas (sin acentos ni espacios),
 * porque además de las plantillas de inicio («Invoices», «Facturas»… con invoice_number,
 * issue_date, supplier_name, supplier_tax_id, total_amount, tax_amount; SIN líneas) hay
 * plantillas de usuario y del modo automático con otros nombres.
 *
 * Cuándo es «de facturas» (botón visible), decidido el 3-11-2026:
 *  - una plantilla de inicio que no sea la de facturas (tickets, presupuestos, albaranes…) nunca;
 *  - si no, hace falta proveedor y total y además un NÚMERO DE FACTURA (no basta la fecha: un
 *    ticket tiene comercio, fecha y total);
 *  - excepción: sin campo de número, vale si el NOMBRE de la plantilla dice factura/invoice/
 *    fattura/Rechnung/fatura/facture y hay fecha.
 *
 * Xero no está: su ayuda (central.xero.com) exige iniciar sesión y no se pudo comprobar el
 * formato oficial de la importación de facturas de proveedor. Sage tampoco (sin formato oficial).
 */

export const norm = (s) =>
  String(s || "")
    .toLowerCase()
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .replace(/ß/g, "ss")
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");

// Rol → nombres posibles del campo (normalizados). El orden manda: el primero que exista gana.
export const FIELD_ALIASES = {
  supplier: ["supplier_name", "supplier", "vendor_name", "vendor", "proveedor", "nombre_proveedor", "nombre_emisor", "emisor", "razon_social", "razon_social_emisor", "fornitore", "ragione_sociale", "fournisseur", "raison_sociale", "lieferant", "rechnungssteller", "fornecedor", "issuer_name", "seller_name", "merchant_name"],
  taxId: ["supplier_tax_id", "supplier_vat", "supplier_vat_number", "vat_number", "tax_id", "nif_proveedor", "cif_proveedor", "nif_emisor", "nif", "cif", "partita_iva", "p_iva", "piva", "siret", "numero_tva", "ust_idnr", "ust_id", "steuernummer"],
  number: ["invoice_number", "invoice_no", "invoice_num", "invoice_id", "bill_number", "bill_no", "numero_factura", "num_factura", "n_factura", "no_factura", "numero_de_factura", "numero_fattura", "n_fattura", "num_fattura", "numero_de_facture", "numero_facture", "n_facture", "rechnungsnummer", "rechnungsnr", "numero_da_fatura", "numero_fatura", "invoice"],
  date: ["issue_date", "invoice_date", "bill_date", "fecha_factura", "fecha_de_factura", "fecha_emision", "fecha_de_emision", "data_fattura", "data_emissione", "date_de_facture", "date_facture", "date_emission", "rechnungsdatum", "data_da_fatura", "data_fatura", "data_emissao", "fecha", "data", "date", "datum"],
  dueDate: ["due_date", "payment_due_date", "fecha_vencimiento", "vencimiento", "scadenza", "data_scadenza", "date_echeance", "echeance", "faelligkeitsdatum", "falligkeitsdatum", "data_vencimento"],
  net: ["net_amount", "net_total", "subtotal", "base_imponible", "base", "imponibile", "totale_imponibile", "montant_ht", "total_ht", "nettobetrag", "netto", "valor_liquido", "base_tributavel", "net"],
  tax: ["tax_amount", "vat_amount", "total_tax", "total_vat", "iva", "cuota_iva", "importe_iva", "total_iva", "imposta", "totale_iva", "tva", "montant_tva", "total_tva", "mwst", "mehrwertsteuer", "ust", "valor_iva", "vat", "tax"],
  total: ["total_amount", "invoice_total", "total_due", "amount_due", "grand_total", "total", "importe_total", "total_factura", "importe", "totale", "totale_fattura", "importo_totale", "montant_ttc", "total_ttc", "gesamtbetrag", "rechnungsbetrag", "bruttobetrag", "valor_total", "amount"],
  currency: ["currency", "moneda", "divisa", "valuta", "devise", "waehrung", "wahrung", "moeda"],
};

// Números que identifican una FACTURA (no «number» ni «reference» a secas: un presupuesto o un
// pedido también los tienen).
const INVOICE_NUMBER_ALIASES = new Set(FIELD_ALIASES.number);
const INVOICE_NAME_RE = /invoice|factur|fattur|rechnung|fatura|bill/i;
const NON_INVOICE_STARTERS = new Set(["receipt", "quote", "deliverynote", "purchaseorder", "id", "bankstatement", "businesscard"]);

// Tablas de líneas por nombre; nunca el desglose de IVA.
const LINE_TABLE_NAMES = ["lines", "line_items", "lineitems", "items", "invoice_lines", "lineas", "lineas_factura", "conceptos", "detalle", "articulos", "righe", "righe_fattura", "articoli", "lignes", "lignes_facture", "articles", "positionen", "rechnungspositionen", "posten", "linhas", "itens", "artigos"];
const TAX_TABLE_RE = /(^|_)(tax|vat|iva|tva|mwst|ust|impuesto|impuestos|imposte|steuer)(_|$)|desglose/;

const fieldsOf = (entry) => entry?.fields || entry?.extractedJson?.fields || [];

// Un valor puede venir envuelto: { value, confidence, source_fragment }.
export const unwrap = (v) => {
  let x = v;
  for (let i = 0; i < 3 && x && typeof x === "object" && !Array.isArray(x) && "value" in x; i += 1) x = x.value;
  return x;
};

const isTableValue = (v) => Array.isArray(v) && v.some((r) => r && typeof r === "object" && !Array.isArray(r));

/** Rol → nombre real del campo, a partir de la definición de la plantilla (o de los resultados). */
export function resolveRoles(fieldNames) {
  const byNorm = new Map();
  (fieldNames || []).forEach((n) => {
    if (n && !byNorm.has(norm(n))) byNorm.set(norm(n), n);
  });
  const roles = {};
  const used = new Set();
  Object.entries(FIELD_ALIASES).forEach(([role, aliases]) => {
    const hit = aliases.find((a) => byNorm.has(a) && !used.has(a));
    if (hit) {
      roles[role] = byNorm.get(hit);
      used.add(hit);
    }
  });
  return roles;
}

/** ¿Hay un número de FACTURA (no un número cualquiera)? */
const hasInvoiceNumber = (roles) => Boolean(roles.number && INVOICE_NUMBER_ALIASES.has(norm(roles.number)));

/** Regla del botón (ver cabecera). `scanner` opcional: nombre y `starter_template`. */
export function isInvoiceRoles(roles, scanner) {
  if (!roles || !roles.supplier || !roles.total) return false;
  const starter = scanner && scanner.starter_template ? String(scanner.starter_template) : "";
  if (NON_INVOICE_STARTERS.has(starter)) return false;
  if (hasInvoiceNumber(roles)) return true;
  return Boolean(!roles.number && roles.date && scanner && INVOICE_NAME_RE.test(String(scanner.name || "")));
}

export function invoiceRolesForScanner(scanner) {
  const names = (scanner?.fields || []).map((f) => f?.name).filter(Boolean);
  return resolveRoles(names);
}

// --------------------------------------------------------------------------------------- importes

// Primer importe del texto: «1 234,56», «1.234,56», «1,234.56», «12,5»… nunca junta dos números.
const AMOUNT_RE = /\d{1,3}(?:[   ']\d{3})+(?:[.,]\d+)?|\d+(?:[.,]\d+)*/;

/**
 * Importe a número. `opts.decimalComma`: el documento usa coma decimal (decide «1.005» = 1005).
 * Sin pista, «1.005» es 1,005 (punto decimal). Un porcentaje («21 %») no es un importe: null.
 */
export function parseAmount(raw, opts = {}) {
  const v = unwrap(raw);
  if (typeof v === "number") return Number.isFinite(v) ? v : null;
  if (v === null || v === undefined || typeof v === "object") return null;
  const s = String(v).trim();
  if (!s) return null;
  // El primer número que no sea un porcentaje («21% 210,00» → 210,00).
  const re = new RegExp(AMOUNT_RE.source, "g");
  let m;
  let after = "";
  for (;;) {
    m = re.exec(s);
    if (!m) return null;
    after = s.slice(m.index + m[0].length);
    if (!/^\s*%/.test(after)) break;
  }
  const before = s.slice(0, m.index);
  // Negativo solo con el «-» justo delante (o detrás, al final), admitiendo un símbolo o código de
  // moneda en medio: «-12,50», «€ -12,50», «-€12,50», «12,50-», «(12,50)». «Total - IVA incl. 121,00»
  // y «Pre-tax: 100» son positivos.
  const CUR = "(?:[€$£¥]|[A-Z]{3})?";
  const negative =
    new RegExp(`-\\s*${CUR}\\s*$`).test(before) ||
    /^\s*-\s*$/.test(after) ||
    (new RegExp(`\\(\\s*${CUR}\\s*$`).test(before) && new RegExp(`^\\s*${CUR}\\s*\\)`).test(after));
  let t = m[0].replace(/[   ']/g, "");
  const lastDot = t.lastIndexOf(".");
  const lastComma = t.lastIndexOf(",");
  if (lastDot >= 0 && lastComma >= 0) {
    // Los dos: el último es el decimal.
    const dec = lastDot > lastComma ? "." : ",";
    t = t.split(dec === "." ? "," : ".").join("").replace(dec, ".");
  } else if (lastComma >= 0) {
    // «12,50» decimal; «1,234» o «1,234,567» miles (salvo que el documento use coma decimal).
    t = /^\d{1,3}(,\d{3})+$/.test(t) && !opts.decimalComma ? t.split(",").join("") : t.split(",").join(".");
  } else if ((t.match(/\./g) || []).length > 1) {
    t = t.split(".").join(""); // «1.234.567»
  } else if (opts.decimalComma && /^[1-9]\d{0,2}\.\d{3}$/.test(t)) {
    t = t.split(".").join(""); // «1.005» en un documento con coma decimal = 1005
  }
  if ((t.match(/\./g) || []).length > 1) return null;
  const n = Number(t);
  if (!Number.isFinite(n)) return null;
  return negative ? -n : n;
}

/** ¿El documento escribe los importes con coma decimal? (algún «12,50» o «1.234,56»). */
export function usesDecimalComma(values) {
  return (values || []).some((raw) => {
    const v = unwrap(raw);
    return typeof v === "string" && /\d,\d{1,2}(?!\d)/.test(v) && !/\d,\d{3}\.\d/.test(v);
  });
}

/** Céntimos sin el error de coma flotante (1.005 → 101, no 100). */
export const toCents = (n) => Math.round((n + Math.sign(n) * 1e-9) * 100);
const money = (c) => (c / 100).toFixed(2);
const round2 = (n) => toCents(n) / 100;

// ------------------------------------------------------------------------------------------ fechas

const pad = (n) => String(n).padStart(2, "0");
const validYmd = (y, m, d) => {
  const dt = new Date(Date.UTC(y, m - 1, d));
  return dt.getUTCFullYear() === y && dt.getUTCMonth() === m - 1 && dt.getUTCDate() === d;
};
const MONTHS = {
  jan: 1, ene: 1, gen: 1, janv: 1, jan_: 1,
  feb: 2, fev: 2, fevr: 2,
  mar: 3, mars: 3, maer: 3, marz: 3,
  apr: 4, abr: 4, avr: 4,
  may: 5, mai: 5, mag: 5, mayo: 5,
  jun: 6, giu: 6, juin: 6,
  jul: 7, lug: 7, juil: 7,
  aug: 8, ago: 8, aou: 8,
  sep: 9, set: 9, sept: 9,
  oct: 10, ott: 10, okt: 10, out: 10,
  nov: 11,
  dec: 12, dic: 12, dez: 12,
};
const monthOf = (word) => {
  const w = norm(word).replace(/_/g, "");
  return MONTHS[w] || MONTHS[w.slice(0, 4)] || MONTHS[w.slice(0, 3)] || null;
};

/**
 * Fecha a ISO (AAAA-MM-DD). Acepta ISO (con hora), AAAA/MM/DD, D/M/AAAA, D.M.AAAA, D-M-AAAA
 * (día primero salvo que no pueda serlo, p. ej. 03/25/2026), «12 Oct 2026», «12-Oct-2026», «12. Oktober 2026»,
 * «October 12, 2026» y «12 de octubre de 2026». Devuelve null si no se entiende.
 */
export function toIsoDate(raw) {
  const v = unwrap(raw);
  if (v === null || v === undefined || v === "" || typeof v === "object") return null;
  const s = String(v).trim();
  const ymd = (y, mo, d) => (validYmd(y, mo, d) ? `${y}-${pad(mo)}-${pad(d)}` : null);
  let m = s.match(/^(\d{4})[-/.](\d{1,2})[-/.](\d{1,2})/);
  if (m) return ymd(Number(m[1]), Number(m[2]), Number(m[3]));
  m = s.match(/^(\d{1,2})[/.-](\d{1,2})[/.-](\d{2}|\d{4})$/);
  if (m) {
    let [a, b, y] = [Number(m[1]), Number(m[2]), Number(m[3])];
    if (y < 100) y += 2000;
    return b > 12 && a <= 12 ? ymd(y, a, b) : ymd(y, b, a);
  }
  // «12 Oct 2026», «12. Oktober 2026», «12 de octubre de 2026»
  m = s.match(/^(\d{1,2})\.?[\s-]+(?:de\s+)?([A-Za-zÀ-ÿ]{3,})\.?,?[\s-]+(?:de\s+)?(\d{4})$/);
  if (m && monthOf(m[2])) return ymd(Number(m[3]), monthOf(m[2]), Number(m[1]));
  // «October 12, 2026», «Oct 12 2026»
  m = s.match(/^([A-Za-zÀ-ÿ]{3,})\.?\s+(\d{1,2})(?:st|nd|rd|th)?,?\s+(\d{4})$/);
  if (m && monthOf(m[1])) return ymd(Number(m[3]), monthOf(m[1]), Number(m[2]));
  return null;
}

/** «AAAA-MM» de cada mes entre dos meses, ambos incluidos. */
export function monthsBetween(fromYm, toYm) {
  let [y, mo] = String(fromYm).split("-").map(Number);
  const [ty, tm] = String(toYm).split("-").map(Number);
  const out = [];
  while ((y < ty || (y === ty && mo <= tm)) && out.length < 240) {
    out.push(`${y}-${pad(mo)}`);
    mo += 1;
    if (mo > 12) {
      mo = 1;
      y += 1;
    }
  }
  return out;
}

/** Los últimos `n` meses («AAAA-MM»), del más reciente al más antiguo. */
export function recentMonths(now = new Date(), n = 13) {
  const out = [];
  for (let i = 0; i < n; i += 1) {
    const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
    out.push(`${d.getFullYear()}-${pad(d.getMonth() + 1)}`);
  }
  return out;
}

/** Inicio del periodo en ISO (para dejar de pedir páginas del historial). */
export const periodStartIso = (fromYm) => `${fromYm}-01`;

// ------------------------------------------------------------------------------------- facturas

const valueMap = (entry) => {
  const map = {};
  fieldsOf(entry).forEach((f) => {
    if (f && f.name && !(f.name in map)) map[f.name] = unwrap(f.value);
  });
  return map;
};

const scalar = (raw) => {
  const v = unwrap(raw);
  if (v === null || v === undefined || typeof v === "object") return "";
  return String(v).trim();
};

const isDone = (entry) => {
  const s = String(entry?.status || "done").toLowerCase();
  return s === "done" || s === "completed";
};

const duplicateOf = (entry) => entry?.extractedJson?.duplicate_of || entry?.duplicate_of || null;

/** Fecha con la que una factura cae en un periodo: la de la factura o, si no se lee, la de subida. */
export function invoicePeriodDate(entry, roles) {
  const fromDoc = roles.date ? toIsoDate(valueMap(entry)[roles.date]) : null;
  if (fromDoc) return { iso: fromDoc, basis: "invoice" };
  const up = entry?.createdAt ? toIsoDate(String(entry.createdAt).slice(0, 10)) : null;
  return { iso: up, basis: "upload" };
}

/** Facturas terminadas cuya fecha cae entre `fromYm` y `toYm` (meses incluidos), sin repetir resultado. */
export function entriesInPeriod(entries, roles, fromYm, toYm) {
  const months = new Set(monthsBetween(fromYm, toYm));
  const seen = new Set();
  return (entries || []).filter((e) => {
    if (!isDone(e) || !fieldsOf(e).length) return false;
    if (e.resultId) {
      if (seen.has(e.resultId)) return false;
      seen.add(e.resultId);
    }
    const { iso } = invoicePeriodDate(e, roles);
    return Boolean(iso && months.has(iso.slice(0, 7)));
  });
}

/** Importes de la factura (base, IVA, total) con el formato decimal del propio documento. */
export function invoiceAmounts(entry, roles) {
  const vals = valueMap(entry);
  const decimalComma = usesDecimalComma(Object.values(vals));
  const get = (role) => (roles[role] ? parseAmount(vals[roles[role]], { decimalComma }) : null);
  const total = get("total");
  const tax = get("tax");
  const netRead = get("net");
  // Base: la impresa si existe; total − IVA solo de respaldo (con IRPF, total − IVA no es la base).
  const net = netRead !== null ? netRead : total !== null && tax !== null ? round2(total - tax) : null;
  return { total, tax, net, netRead, decimalComma };
}

/** Tabla de líneas de la factura: por nombre (lines, items, lineas, righe…), nunca el desglose de IVA. */
export function lineTableOf(entry) {
  const tables = fieldsOf(entry).filter((f) => f?.name && isTableValue(unwrap(f.value)) && !TAX_TABLE_RE.test(norm(f.name)));
  const byName = tables.find((f) => LINE_TABLE_NAMES.includes(norm(f.name)));
  return byName || tables[0] || null;
}

export const CHANNEL_KEYS = { whatsapp: "whatsapp", email: "email", telegram: "telegram", api: "api", free_demo_import: "trial" };
export const channelOf = (entry) => CHANNEL_KEYS[String(entry?.sourceType || "").toLowerCase()] || "web";

/** Clave de duplicado exacto: proveedor + número + total. */
const dupKey = (supplier, number, totalCents) => `${norm(supplier)}|${norm(number)}|${totalCents}`;

// Texto apto para XML/CSV: sin sustitutos sueltos ni U+FFFE/U+FFFF.
export const cleanText = (s) =>
  String(s)
    .replace(/[\uD800-\uDBFF](?![\uDC00-\uDFFF])|(^|[^\uD800-\uDBFF])[\uDC00-\uDFFF]/g, "$1")
    .replace(/[￾￿]/g, "");

const humanize = (k) => String(k || "").replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

/**
 * Hojas del Excel del periodo.
 * `labels`: textos de cabecera ya traducidos (supplier, taxId, number, date, dueDate, net, tax,
 * total, currency, file, channel, uploaded, dateBasis, basisInvoice, basisUpload, duplicate, yes).
 * `opts.channelLabel(key)` traduce el canal; `opts.scannerFields` (definición de la plantilla) da las
 * etiquetas de los campos propios y de las columnas de líneas.
 * El nombre de archivo SÍ va: es el Excel del propio usuario y sirve para encontrar el original
 * (en los eventos del pixel nunca se manda). Las fechas van como { date: ISO } (fecha de Excel).
 */
export function buildMonthSheets(entries, roles, labels = {}, opts = {}) {
  const L = (k) => labels[k] || k;
  const channelLabel = opts.channelLabel || ((k) => k);
  const defs = opts.scannerFields || [];
  const defLabel = (name) => {
    const d = defs.find((f) => f && f.name === name);
    return (d && (d.label || d.display_name)) || null;
  };
  const headRoles = ["supplier", "taxId", "number", "date", "dueDate", "net", "tax", "total", "currency"].filter((r) => roles[r]);
  const roleFields = new Set(Object.values(roles));
  // Campos propios de la plantilla no reconocidos (escalares): columnas extra.
  const extra = [];
  const extraLabel = {};
  (entries || []).forEach((e) => {
    fieldsOf(e).forEach((f) => {
      if (!f || !f.name || roleFields.has(f.name) || extra.includes(f.name) || isTableValue(unwrap(f.value))) return;
      extra.push(f.name);
      extraLabel[f.name] = defLabel(f.name) || f.label || humanize(f.name);
    });
  });
  const header = [...headRoles.map(L), ...extra.map((n) => extraLabel[n]), L("dateBasis"), L("duplicate"), L("file"), L("channel"), L("uploaded")];
  const rows = [header];
  const lineRows = [];
  const lineCols = [];
  const lineColLabel = {};
  const sorted = [...(entries || [])].sort((a, b) => {
    const da = invoicePeriodDate(a, roles).iso || "";
    const db = invoicePeriodDate(b, roles).iso || "";
    return da < db ? -1 : da > db ? 1 : 0;
  });
  const seenDup = new Set();
  sorted.forEach((entry) => {
    const vals = valueMap(entry);
    const amounts = invoiceAmounts(entry, roles);
    const cell = (role) => {
      const v = vals[roles[role]];
      if (role === "net") return amounts.netRead !== null ? String(round2(amounts.netRead)) : scalar(v);
      if (role === "tax") return amounts.tax !== null ? String(round2(amounts.tax)) : scalar(v);
      if (role === "total") return amounts.total !== null ? String(round2(amounts.total)) : scalar(v);
      if (role === "date" || role === "dueDate") {
        const iso = toIsoDate(v);
        return iso ? { date: iso } : cleanText(scalar(v));
      }
      return cleanText(scalar(v));
    };
    const { basis } = invoicePeriodDate(entry, roles);
    const key = dupKey(scalar(vals[roles.supplier]), scalar(vals[roles.number]), amounts.total === null ? "" : toCents(amounts.total));
    const isDup = Boolean(duplicateOf(entry)) || (roles.number && scalar(vals[roles.number]) && seenDup.has(key));
    seenDup.add(key);
    rows.push([
      ...headRoles.map(cell),
      ...extra.map((n) => cleanText(scalar(vals[n]))),
      basis === "invoice" ? L("basisInvoice") : L("basisUpload"),
      isDup ? L("yes") : "",
      cleanText(entry.fileName || entry.filename || ""),
      channelLabel(channelOf(entry)),
      entry.createdAt ? { date: String(entry.createdAt).slice(0, 10) } : "",
    ]);
    const table = lineTableOf(entry);
    if (!table) return;
    const def = defs.find((f) => f && f.name === table.name);
    const itemLabel = (k) => {
      const it = (def?.item_fields || []).find((x) => x && x.name === k);
      return (it && it.label) || humanize(k);
    };
    unwrap(table.value).forEach((r) => {
      if (!r || typeof r !== "object" || Array.isArray(r)) return;
      Object.keys(r).forEach((k) => {
        if (!lineCols.includes(k)) {
          lineCols.push(k);
          lineColLabel[k] = itemLabel(k);
        }
      });
      lineRows.push({ key: [cell("supplier"), cell("number"), cell("date")], row: r });
    });
  });
  const lines = [[L("supplier"), L("number"), L("date"), ...lineCols.map((k) => lineColLabel[k])]];
  lineRows.forEach(({ key, row }) => {
    lines.push([
      ...key,
      ...lineCols.map((k) => {
        const v = unwrap(row[k]);
        if (v && typeof v === "object") return "";
        return typeof v === "number" ? String(v) : cleanText(scalar(v));
      }),
    ]);
  });
  return { headers: rows, lines, lineCount: lineRows.length };
}

// ---------------------------------------------------------------------------------------
// QuickBooks Online — importación de facturas de proveedor («Bills»).
// Ayuda oficial (comprobada el 3-11-2026):
// https://quickbooks.intuit.com/learn-support/en-uk/help-article/import-transactions/import-bills-quickbooks-online/L4Q6QWsRw_GB_en_GB
// Columnas obligatorias: «Bill no.», «Supplier», «Bill Date», «Due Date», «Account», «Line Amount»
// y «Line Tax Code» (esta solo con el IVA activado). Las cabeceras se asignan a mano al importar,
// el formato de fecha se elige (el ejemplo es D/M/AAAA) y se indica si los importes llevan IVA.
// Aquí todo va SIN IVA (al importar hay que elegir «Exclusive of VAT», el mismo para todo el
// fichero) y el IVA leído va en «Line Tax Amount» (QuickBooks usa el importe dado en vez de
// calcularlo). Si la factura trae líneas con importe y suman la base (±0,02), una fila por línea
// con el IVA repartido en proporción (redondeo al céntimo, el resto en la última); si no, una
// fila por factura con la base. Se saltan, contadas por motivo: sin proveedor/total/IVA legible
// («missing»), posibles duplicados (`duplicate_of` del procesador cuyo original sigue en el
// historial, o proveedor + número + total repetidos: «duplicate») y abonos (total o base negativos,
// o IVA mayor que el total: «credit_note»; QuickBooks los trata como «supplier credit», otra
// importación). QuickBooks funde las filas con el mismo «Bill no.»: el mismo número en proveedores
// distintos lleva un sufijo (4 últimos caracteres del NIF o abreviatura del proveedor, únicos) y el
// mismo proveedor y número con otro total, -2, -3… Máximo recomendado: 100 facturas por importación.
// ---------------------------------------------------------------------------------------
export const QBO_HEADERS = ["Bill no.", "Supplier", "Bill Date", "Due Date", "Account", "Line Description", "Line Amount", "Line Tax Code", "Line Tax Amount"];
export const QBO_MAX_BILLS = 100;

/**
 * Neutraliza lo que una hoja de cálculo ejecutaría como fórmula: empieza por «=» o «@», o por «+»/«-»
 * seguido de algo que no sea un número ni un espacio («-A1+B1», «+cmd|…»). «- Filtro», «-12,50» y
 * «+34 600 000 000» se quedan como están.
 */
export function neutralizeFormula(s) {
  const t = String(s);
  if (/^[=@\t\r]/.test(t)) return `'${t}`;
  if (/^[+-](?![\s\d])/.test(t)) return `'${t}`;
  if (/^[+-]\d/.test(t) && /[=(]|[A-Za-z]{1,3}\d/.test(t)) return `'${t}`;
  return t;
}

const csvCell = (v) => {
  const s = neutralizeFormula(cleanText(v === null || v === undefined ? "" : v));
  return /[",\r\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
};

const dmy = (iso) => (iso ? `${iso.slice(8, 10)}/${iso.slice(5, 7)}/${iso.slice(0, 4)}` : "");

const LINE_AMOUNT_KEYS = ["amount", "line_total", "line_amount", "total", "importe", "importe_linea", "total_linea", "net_amount", "importo", "montant", "montant_ht", "betrag", "gesamtpreis", "valor"];
const LINE_DESC_KEYS = ["description", "descripcion", "concepto", "item", "product", "producto", "descrizione", "designation", "beschreibung", "bezeichnung", "descricao", "name"];

/** Líneas con importe (sin IVA), solo si suman la base con ±0,02; si no, null (no se inventa un reparto). */
export function billLines(entry, netCents, decimalComma = false) {
  const table = lineTableOf(entry);
  if (!table) return null;
  const out = [];
  for (const r of unwrap(table.value)) {
    if (!r || typeof r !== "object" || Array.isArray(r)) continue;
    const byNorm = {};
    Object.keys(r).forEach((k) => {
      byNorm[norm(k)] = unwrap(r[k]);
    });
    const ak = LINE_AMOUNT_KEYS.find((k) => k in byNorm && parseAmount(byNorm[k], { decimalComma }) !== null);
    if (!ak) return null;
    const dk = LINE_DESC_KEYS.find((k) => k in byNorm && scalar(byNorm[k]));
    out.push({ cents: toCents(parseAmount(byNorm[ak], { decimalComma })), desc: dk ? scalar(byNorm[dk]) : "" });
  }
  if (!out.length) return null;
  const sum = out.reduce((s, l) => s + l.cents, 0);
  return Math.abs(sum - netCents) <= 2 ? out : null;
}

/** Ids y subida más antigua de lo cargado del historial. */
export function loadedInfo(items) {
  const ids = new Set();
  let oldest = "";
  (items || []).forEach((it) => {
    if (it?.resultId) ids.add(it.resultId);
    const c = String(it?.createdAt || "");
    if (c && (!oldest || c < oldest)) oldest = c;
  });
  return { ids, oldest };
}

/**
 * ¿El original de un `duplicate_of` sigue existiendo? Sí si está entre lo cargado, o si es anterior a
 * lo cargado (no se ha mirado: se da por bueno). Si debería estar en lo cargado y no está, el usuario
 * lo borró: esta factura ya no es un duplicado y va a QuickBooks (en el Excel sigue marcada).
 */
export function originalPresent(dup, loaded) {
  if (!dup) return false;
  if (dup.result_id && loaded.ids.has(dup.result_id)) return true;
  return Boolean(dup.created_at && loaded.oldest && String(dup.created_at) < loaded.oldest);
}

const suffixFor = (supplier, taxId) => {
  const id = String(taxId || "").replace(/[^A-Za-z0-9]/g, "");
  if (id.length >= 4) return id.slice(-4).toUpperCase();
  const words = norm(supplier).split("_").filter(Boolean);
  const abbr = words.length > 1 ? words.map((w) => w[0]).join("") : words[0] || "X";
  return abbr.slice(0, 4).toUpperCase();
};

/**
 * CSV de facturas de proveedor para QuickBooks Online, importes SIN IVA. `opts.account` (cuenta de
 * gastos tal cual existe en QuickBooks) es obligatorio; `opts.taxCode` solo si el IVA está activado.
 * Devuelve { csv, bills, rows, skipped, skippedBy: {missing, duplicate, credit_note}, renamed }.
 */
export function buildQboCsv(entries, roles, opts = {}) {
  const account = String(opts.account || "").trim();
  const taxCode = String(opts.taxCode || "").trim();
  const skippedBy = { missing: 0, duplicate: 0, credit_note: 0 };
  const prepared = [];
  const seen = new Set();
  // Lo cargado del historial (ids y la subida más antigua), para decidir si el original de un
  // `duplicate_of` sigue ahí. Sin `opts.loaded`, lo que se pasa en `entries`.
  const loaded = opts.loaded || loadedInfo(entries);
  (entries || []).forEach((entry, i) => {
    const vals = valueMap(entry);
    const supplier = cleanText(scalar(vals[roles.supplier]));
    const { total, tax, net } = invoiceAmounts(entry, roles);
    if (!supplier || total === null || net === null || (tax === null && !roles.net)) {
      skippedBy.missing += 1;
      return;
    }
    const totalC = toCents(total);
    const netC = toCents(net);
    const taxC = tax !== null ? toCents(tax) : totalC - netC;
    if (totalC < 0 || netC < 0 || taxC > totalC) {
      skippedBy.credit_note += 1;
      return;
    }
    const number = cleanText(scalar(vals[roles.number])) || `WS-${String(entry.resultId || i + 1).slice(0, 8)}`;
    const key = dupKey(supplier, number, totalC);
    if (originalPresent(duplicateOf(entry), loaded) || seen.has(key)) {
      skippedBy.duplicate += 1;
      return;
    }
    seen.add(key);
    prepared.push({ entry, vals, supplier, number, netC, taxC });
  });
  // Números de factura únicos para QuickBooks (que funde las filas con el mismo «Bill no.»):
  //  - el mismo número en proveedores distintos lleva el sufijo del proveedor (únicos dentro del grupo);
  //  - el mismo proveedor y número con otro total (no es un duplicado exacto) lleva -2, -3…
  const groups = new Map();
  prepared.forEach((p) => {
    const k = norm(p.number);
    if (!groups.has(k)) groups.set(k, []);
    groups.get(k).push(p);
  });
  let renamed = 0;
  let numbered = 0;
  groups.forEach((list) => {
    const suppliers = [...new Set(list.map((p) => norm(p.supplier)))];
    const suffix = new Map();
    if (suppliers.length > 1) {
      const used = new Set();
      suppliers.forEach((s) => {
        const p = list.find((x) => norm(x.supplier) === s);
        const base = suffixFor(p.supplier, roles.taxId ? scalar(p.vals[roles.taxId]) : "");
        let sfx = base;
        for (let n = 2; used.has(sfx); n += 1) sfx = `${base}${n}`;
        used.add(sfx);
        suffix.set(s, sfx);
      });
    }
    const ordinal = new Map();
    list.forEach((p) => {
      const s = norm(p.supplier);
      const n = (ordinal.get(s) || 0) + 1;
      ordinal.set(s, n);
      p.billNo = p.number;
      if (suffix.has(s)) {
        p.billNo += `-${suffix.get(s)}`;
        renamed += 1;
      }
      if (n > 1) {
        p.billNo += `-${n}`;
        numbered += 1;
      }
    });
  });
  const rows = [QBO_HEADERS];
  prepared.forEach(({ entry, vals, supplier, number, billNo, netC, taxC }) => {
    const date = invoicePeriodDate(entry, roles).iso;
    const due = (roles.dueDate && toIsoDate(vals[roles.dueDate])) || date;
    const head = [billNo, supplier, dmy(date), dmy(due), account];
    const lines = billLines(entry, netC, invoiceAmounts(entry, roles).decimalComma);
    if (!lines) {
      rows.push([...head, `${supplier} ${number}`.slice(0, 100), money(netC), taxCode, money(taxC)]);
      return;
    }
    // IVA repartido en proporción a cada línea; el redondeo sobrante va a la última.
    let given = 0;
    lines.forEach((l, li) => {
      const share = li === lines.length - 1 ? taxC - given : netC ? Math.round((taxC * l.cents) / netC) : 0;
      given += share;
      rows.push([...head, (l.desc || `${supplier} ${number}`).slice(0, 100), money(l.cents), taxCode, money(share)]);
    });
  });
  const csv = "﻿" + rows.map((r) => r.map(csvCell).join(",")).join("\r\n") + "\r\n";
  const skipped = skippedBy.missing + skippedBy.duplicate + skippedBy.credit_note;
  return { csv, bills: prepared.length, rows: rows.length - 1, skipped, skippedBy, renamed, numbered };
}
